import React from 'react';
import { useEdgesState, useNodesState, useReactFlow } from '@xyflow/react';

import { baseHouseHeight } from './constants';

const AccomodationDiagramContext = React.createContext([null, _ => {}]);

function groupBeds(beds) {
  // Obiekty, które mają przypisany relatedGroup
  const grouped = beds.filter(obj => obj.data && obj.data.order && obj.data.order.relatedGroup);

  // Sortowanie obiektów według relatedGroup.id
  grouped.sort((a, b) => a.data.order.relatedGroup.id.localeCompare(b.data.order.relatedGroup.id));

  // Obiekty, które nie mają przypisanego relatedGroup lub obj.data/order są nullem
  const ungrouped = beds.filter(obj => !(obj.data && obj.data.order && obj.data.order.relatedGroup));

  // Łączenie obiektów: najpierw te z grupami, potem pozostałe
  return [...grouped, ...ungrouped];
}

export const AccomodationDiagramProvider = ({ children }) => {
  const { getNodes } = useReactFlow();

  const [selectedAccomodationDiagram, setSelectedAccomodationDiagram] = React.useState(null);
  const [order, setOrder] = React.useState(null);
  const [eventRecord, setEventRecord] = React.useState(null);
  const [selectedEventGroup, setSelectedEventGroup] = React.useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [events, setEvents] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [initOrders, setInitOrders] = React.useState(null);
  const [relatedGroups, setRelatedGroups] = React.useState([]);
  const [showOrderDetails, setShowOrderDetails] = React.useState(false);
  const [showRelatedOrdersMenu, setShowRelatedOrdersMenu] = React.useState(false);
  const [showManageRelatedOrdersMenu, setShowManageRelatedOrdersMenu] = React.useState(false);
  const [showEventRecordGroupMenu, setShowEventRecordGroupMenu] = React.useState(false);
  const [showComments, setShowComments] = React.useState(false);
  // Albo null albo lista ID domków
  const [recalculateHousePositions, setRecalculateHousePositions] = React.useState(null);
  const [recalculateAllHouses, setRecalculateAllHouses] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [houseOrders, setHouseOrders] = React.useState(null);
  const [showDiagramDataSyncDialog, setShowDiagramDataSyncDialog] = React.useState(false);
  const [showOwnershipDialog, setShowOwnershipDialog] = React.useState(false);
  const [savingDisabled, setSavingDisabled] = React.useState(false);
  const [queryOrdersSignal, setQueryOrdersSignal] = React.useState(false);
  const [showChangeAgeRangeDialog, setShowChangeAgeRangeDialog] = React.useState(false);
  const [changedPrefixes, setChangedPrefixes] = React.useState({});
  const [showAccommodationPrefixDialog, setShowAccommodationPrefixDialog] = React.useState(false);
  const [showOrderAccommodationPrefixDialog, setShowOrderAccommodationPrefixDialog] = React.useState(false);

  const addNodesF = React.useCallback(
    (newNodes, parentId = null) => {
      setNodes(prevNodes => {
        if (parentId) {
          const parentIndex = prevNodes.findIndex(node => node.id === parentId);

          if (parentIndex === -1) {
            // Jeśli nie znaleziono parentId, dodaj nowe węzły na końcu
            return [...prevNodes, ...newNodes];
          }

          // Wstaw nowe węzły zaraz po parentId
          const updatedNodes = [
            ...prevNodes.slice(0, parentIndex + 1),
            ...newNodes,
            ...prevNodes.slice(parentIndex + 1)
          ];

          return updatedNodes;
        }

        // Jeśli parentId nie jest podane, dodaj nowe węzły na końcu
        return [...prevNodes, ...newNodes];
      });
    },
    [setNodes]
  );

  const updateNodeF = React.useCallback(updatedNode => {
    setNodes(nds => {
      const nodeExists = nds.some(node => node.id === updatedNode.id);

      if (nodeExists) {
        // Aktualizacja istniejącego węzła
        return nds.map(node => (node.id === updatedNode.id ? updatedNode : node));
      }
      // Dodanie nowego węzła, gdy nie istnieje (opcjonalnie)

      return [...nds, updatedNode];
    });
  }, [setNodes]);

  const updateNodesF = React.useCallback(updatedNodes => {
    setNodes(nds => {
      const updatedNodeIds = new Set(updatedNodes.map(node => node.id));

      return nds
        .map(node => updatedNodeIds.has(node.id)
          ? updatedNodes.find(updatedNode => updatedNode.id === node.id)
          : node
        )
        .concat(
          updatedNodes.filter(updatedNode => !nds.some(node => node.id === updatedNode.id))
        );
    });
  }, [setNodes]);

  const removeNode = React.useCallback(
    nodeId => {
      setNodes(prevNodes => {
        // Znajdź wszystkie węzły potomne, które mają parentId równe nodeId
        const findChildNodes = (parentId, nodes) => nodes.filter(
          node => node.parentId === parentId).reduce(
          (acc, childNode) => [...acc, childNode, ...findChildNodes(childNode.id, nodes)], []);

        // Znajdź wszystkie węzły potomne dla usuwanego nodeId
        const childNodes = findChildNodes(nodeId, prevNodes);

        // Usuwamy zarówno węzeł główny, jak i jego potomków
        return prevNodes.filter(node => node.id !== nodeId && !childNodes.includes(node));
      });
    },
    [setNodes]
  );
  const recalculatePositions = () => {
    const nodesToUpdate = [];
    recalculateHousePositions.map(houseIdToRecalculate => {
      const houseId = houseIdToRecalculate;
      const houseNode = getNodes().find(n => n.id === houseId);
      if (!houseNode) {
        return;
      }
      const eventNodes = getNodes()
        .filter(n => n.parentId === houseId) // Filtruj węzły na podstawie parentId
        .filter((node, index, self) => index === self.findIndex(n => n.id === node.id) // Pozbywamy się zduplikowanych węzłów
        );
      const totalBeds = [];
      eventNodes.forEach(en => {
        const beds = getNodes().filter(n => n.parentId === en.id)
          .filter((node, index, self) => index === self.findIndex(n => n.id === node.id)
          );
        totalBeds.push(...beds);
        const groupedBeds = groupBeds(beds);

        en.style.height = 40 + (38 * beds.length);
        let axisYShift = 34;
        let prevBed = null;
        groupedBeds.forEach((b, key) => {
          b.position.y = axisYShift;
          b.data.slot = key + 1;
          axisYShift += 38;
          nodesToUpdate.push(b);
          // setTimeout(() => {
          //   updateNodeF(b);
          // }, 50);
          prevBed = b;
        });
      });
      // Wysokość domku jednak tylko po ilości łóżek
      const houseNodeHeight = baseHouseHeight(totalBeds.length, eventNodes.length);
      if (houseNode.style.height != houseNodeHeight) {
        houseNode.style.height = houseNodeHeight;
        nodesToUpdate.push(houseNode);
      }
      // setTimeout(() => {
      //   updateNodeF(houseNode);
      // }, 50);

      let axisYShift = 20;
      eventNodes.forEach(en => {
        en.position = {
          x: 10,
          y: axisYShift
        };
        nodesToUpdate.push(en);
        // setTimeout(() => {
        //   updateNodeF(en);
        // }, 50);
        axisYShift += en.style.height + 20;
      });
    });
    updateNodesF(nodesToUpdate);
  };


  const handleRelatedGroups = (newOrders = null, newNodes = null) => {
    const newRelatedGroups = [];
    const ordersF = newOrders ? newOrders : orders;
    const nodesF = newNodes ? newNodes : nodes;
    const accommodatedBeds = nodesF.filter(n => n.type == 'BedNode' && n.data?.order);
    ordersF.forEach(o => {
      if (o.relatedGroup) {
        const relGroup = newRelatedGroups.find(rg => rg.id === o.relatedGroup.id);
        if (relGroup) {
          if (!relGroup.orders.includes(o.identifier)) {
            relGroup.orders.push(o.identifier);
          }
        } else {
          const newRelatedGroup = { ...o.relatedGroup, orders: [o.identifier] };
          newRelatedGroups.push(newRelatedGroup);
        }
      }
    });
    accommodatedBeds.forEach(n => {
      if (n.data.order.relatedGroup) {
        const relGroup = newRelatedGroups.find(rg => rg.id === n.data.order.relatedGroup.id);
        if (relGroup) {
          if (!relGroup.orders.includes(n.data.order.identifier)) {
            relGroup.orders.push(n.data.order.identifier);
          }
        } else {
          const newRelatedGroup = { ...n.data.order.relatedGroup, orders: [n.data.order.identifier] };
          newRelatedGroups.push(newRelatedGroup);
        }
      }
    });
    setRelatedGroups(newRelatedGroups);
  };

  React.useEffect(() => {
    if (selectedAccomodationDiagram) {
      setNodes(selectedAccomodationDiagram.diagram_data.nodes);
      setEvents(selectedAccomodationDiagram.diagram_data.events);
      if (selectedAccomodationDiagram.diagram_data.edges) {
        setEdges(selectedAccomodationDiagram.diagram_data.edges);
      }
      if (selectedAccomodationDiagram.diagram_data.related_groups) {
        setRelatedGroups(selectedAccomodationDiagram.diagram_data.related_groups);
      }
      setRecalculateAllHouses(selectedAccomodationDiagram.should_recalculate_houses_positions);

    }
  }, [selectedAccomodationDiagram]);

  React.useEffect(() => {
    if (recalculateHousePositions) {
      recalculatePositions();
      setRecalculateHousePositions(null);
    }
  }, [getNodes()]);

  React.useEffect(() => {
    if (recalculateAllHouses) {
      const allHousesIds = nodes.filter(n => n.type == 'HouseNode').map(n => n.id);
      setRecalculateHousePositions(allHousesIds);
      setRecalculateAllHouses(false);
    }

  }, [recalculateAllHouses]);

  return (
    <AccomodationDiagramContext.Provider value={
      { order,
        setOrder,
        orders,
        setOrders,
        eventRecord,
        setEventRecord,
        addNodesF,
        updateNodeF,
        updateNodesF,
        removeNode,
        nodes,
        setNodes,
        onNodesChange,
        edges,
        setEdges,
        onEdgesChange,
        events,
        setEvents,
        relatedGroups,
        setRelatedGroups,
        handleRelatedGroups,
        selectedAccomodationDiagram,
        setSelectedAccomodationDiagram,
        showOrderDetails,
        setShowOrderDetails,
        showRelatedOrdersMenu,
        setShowRelatedOrdersMenu,
        showManageRelatedOrdersMenu,
        setShowManageRelatedOrdersMenu,
        setRecalculateHousePositions,
        showComments,
        setShowComments,
        showEventRecordGroupMenu,
        setShowEventRecordGroupMenu,
        selectedEventGroup,
        setSelectedEventGroup,
        isSaving,
        setIsSaving,
        houseOrders,
        setHouseOrders,
        recalculateAllHouses,
        setRecalculateAllHouses,
        showDiagramDataSyncDialog,
        setShowDiagramDataSyncDialog,
        showOwnershipDialog,
        setShowOwnershipDialog,
        savingDisabled,
        setSavingDisabled,
        queryOrdersSignal,
        setQueryOrdersSignal,
        showChangeAgeRangeDialog,
        setShowChangeAgeRangeDialog,
        changedPrefixes,
        setChangedPrefixes,
        showAccommodationPrefixDialog,
        setShowAccommodationPrefixDialog,
        showOrderAccommodationPrefixDialog,
        setShowOrderAccommodationPrefixDialog,
        initOrders,
        setInitOrders }}>
      {children}
    </AccomodationDiagramContext.Provider>
  );
};

export default AccomodationDiagramContext;

export const useAccomodationDiagram = () => React.useContext(AccomodationDiagramContext);
